import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { getDefaultSection, getDefaultTopic, sectionsForUser } from './helpData.js';
import { useAuth } from '../../context/AuthContext.jsx';
import HelpNav from './HelpNav.jsx';
import HelpContent from './HelpContent.jsx';
import './help.css';

/**
 * Slide-in help drawer. Opens from the right, closes on backdrop click or
 * Escape, and locks body scroll while open. Its default section/topic follow
 * the current route so the help opens on the most relevant page.
 *
 * Connectors see only the sections that apply to them (QA 8078) — the staff
 * guides for Referral Ops, Finance, Compliance and Administration describe
 * screens a connector cannot reach at all.
 */
export default function HelpDrawer({ isOpen, onClose }) {
  const location = useLocation();
  const { user } = useAuth();
  const sections = useMemo(() => sectionsForUser(user), [user]);
  // The route's default section may be one this user can't see; fall back to
  // the first section they can, so the drawer never opens on a blank panel.
  const anchorFor = (pathname) => {
    const preferred = getDefaultSection(pathname);
    return sections.some((s) => s.id === preferred) ? preferred : sections[0]?.id;
  };

  const [activeSection, setActiveSection] = useState(() => anchorFor(location.pathname));
  const [activeTopic, setActiveTopic] = useState(() => getDefaultTopic(location.pathname));

  // Re-anchor to the current route whenever the drawer is (re)opened.
  useEffect(() => {
    if (isOpen) {
      const next = anchorFor(location.pathname);
      setActiveSection(next);
      const topic = getDefaultTopic(location.pathname);
      const section = sections.find((s) => s.id === next);
      setActiveTopic(
        section?.topics.some((t) => t.id === topic) ? topic : section?.topics[0]?.id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, location.pathname, sections]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Body scroll while this drawer is open is handled app-wide by
  // useModalScrollLock (QA 8384), keyed on the aria-modal="true" overlay below.
  // A second owner of body.style.overflow here would clear the lock out from
  // under another dialog that is still up.

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    const section = sections.find((s) => s.id === sectionId);
    if (section?.topics.length) setActiveTopic(section.topics[0].id);
  };

  const currentSection = sections.find((s) => s.id === activeSection);
  if (!isOpen) return null;

  // Portalled to <body> deliberately (QA 8080). The help button lives inside
  // <nav class="navbar fixed-top">, and Bootstrap's .fixed-top (position:fixed,
  // z-index:1030) opens a stacking context — so rendering in place trapped this
  // overlay inside it and the connector bottom nav (z-index 1050, a sibling of
  // the navbar) painted over the bottom of the drawer. No z-index on the drawer
  // could win that, because the comparison never reached the top level.
  return createPortal(
    <div className="help-overlay" role="dialog" aria-modal="true" aria-label="Help and Documentation">
      <div className="help-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="help-drawer" data-testid="help-drawer">
        <div className="help-drawer__head">
          <h2 className="help-drawer__title"><i className="fa-solid fa-circle-question me-2" aria-hidden="true" />Help &amp; Documentation</h2>
          <button type="button" className="help-drawer__close" onClick={onClose} aria-label="Close help">
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
        <HelpNav sections={sections} activeSection={activeSection} onSectionChange={handleSectionChange} />
        {currentSection && (
          <HelpContent section={currentSection} activeTopic={activeTopic} onTopicChange={setActiveTopic} />
        )}
      </aside>
    </div>,
    document.body
  );
}
